import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Divider,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Stack
} from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import { AGENCY_TYPES, getAgencyTypeLabel } from '../../../constants/agencyTypes';

const MasterDataFormDialog = ({
  open,
  onClose,
  selectedItem,
  effectiveType,
  currentType,
  manufacturerSubTab,
  formData,
  setFormData,
  onSubmit,
  assemblyTypes,
  manufacturers,
  models,
  ataChapters,
  manufacturerNames,
  manufacturerCategories,
  assemblyTypeConfigurations,
  partCategories,
  handleAddColumn,
  handleRemoveColumn,
  handleColumnChange
}) => {
  // For assembly-type-configurations, derive selected ATA Chapter from Assembly Type
  const getSelectedAtaChapter = () => {
    if (currentType.key !== 'assembly-type-configurations') return null;
    
    // If we have a selected ATA Chapter in formData, use it
    if (formData.selectedAtaChapterId) {
      return ataChapters.find(ch => String(ch._id) === String(formData.selectedAtaChapterId)) || null;
    }
    
    // Otherwise, derive from the selected Assembly Type
    if (formData.assemblyType) {
      const assemblyTypeId = typeof formData.assemblyType === 'object' && formData.assemblyType?._id
        ? formData.assemblyType._id
        : formData.assemblyType;
      const selectedAssemblyType = assemblyTypes.find(at => String(at._id) === String(assemblyTypeId));
      if (selectedAssemblyType?.ataChapterId) {
        const ataChapterId = typeof selectedAssemblyType.ataChapterId === 'object' && selectedAssemblyType.ataChapterId?._id
          ? selectedAssemblyType.ataChapterId._id
          : selectedAssemblyType.ataChapterId;
        return ataChapters.find(ch => String(ch._id) === String(ataChapterId)) || null;
      }
    }
    return null;
  };

  const selectedAtaChapter = getSelectedAtaChapter();
  const selectedAtaChapterId = selectedAtaChapter ? String(selectedAtaChapter._id) : (formData.selectedAtaChapterId || '');

  const scopeLevel = formData.scopeLevel || 'GLOBAL';

  // State to track Common Dataset dropdown selections for each column
  const [commonDatasetSelections, setCommonDatasetSelections] = useState({});
  const [letterpadUploading, setLetterpadUploading] = useState(false);

  // Reset dropdown selections when columns change or dialog opens/closes
  useEffect(() => {
    if (formData.columns && Array.isArray(formData.columns)) {
      const newSelections = {};
      formData.columns.forEach((_, index) => {
        newSelections[index] = '';
      });
      setCommonDatasetSelections(newSelections);
    } else {
      setCommonDatasetSelections({});
    }
  }, [formData.columns?.length, open]);

  // Filter assembly types based on selected ATA Chapter
  const getFilteredAssemblyTypes = () => {
    if (currentType.key !== 'assembly-type-configurations') return assemblyTypes;
    
    if (!selectedAtaChapterId) {
      return []; // No assembly types shown until ATA Chapter is selected
    }
    
    return assemblyTypes.filter(at => {
      const atAtaChapterId = at.ataChapterId 
        ? (typeof at.ataChapterId === 'object' && at.ataChapterId?._id
            ? String(at.ataChapterId._id)
            : String(at.ataChapterId))
        : null;
      
      // For "Airframe" assembly type, it might not have an ATA Chapter
      // Show it only if it matches the selected ATA Chapter or has no ATA Chapter (show always)
      if (at.name?.toLowerCase() === 'airframe') {
        // Show Airframe if it has no ATA Chapter or if it matches the selected one
        return !atAtaChapterId || atAtaChapterId === selectedAtaChapterId;
      }
      
      // For other assembly types, only show if they match the selected ATA Chapter
      return atAtaChapterId === selectedAtaChapterId;
    });
  };

  const filteredAssemblyTypes = getFilteredAssemblyTypes();

  const handleAtaChapterChange = (event, newValue) => {
    const newAtaChapterId = newValue ? String(newValue._id) : '';
    setFormData({ 
      ...formData, 
      selectedAtaChapterId: newAtaChapterId,
      // Clear assembly type when ATA Chapter changes (unless editing)
      ...(selectedItem ? {} : { assemblyType: '' })
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth={currentType.key === 'assembly-type-configurations' ? 'md' : 'sm'} 
      fullWidth
    >
      <DialogTitle>
        {selectedItem ? `Edit ${effectiveType.label.slice(0, -1)}` : `Add ${effectiveType.label.slice(0, -1)}`}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {currentType.key === 'agencies' ? (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Code"
                  value={formData.code || ''}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Agency types (at least one required)</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {AGENCY_TYPES.map((typeValue) => (
                    <FormControlLabel
                      key={typeValue}
                      control={
                        <Checkbox
                          checked={Array.isArray(formData.types) && formData.types.includes(typeValue)}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...(formData.types || []), typeValue]
                              : (formData.types || []).filter((t) => t !== typeValue);
                            setFormData({ ...formData, types: next });
                          }}
                        />
                      }
                      label={getAgencyTypeLabel(typeValue)}
                    />
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.contactInfo?.email || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    contactInfo: { ...(formData.contactInfo || {}), email: e.target.value }
                  })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={formData.contactInfo?.phone || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    contactInfo: { ...(formData.contactInfo || {}), phone: e.target.value }
                  })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Address"
                  value={formData.contactInfo?.address || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    contactInfo: { ...(formData.contactInfo || {}), address: e.target.value }
                  })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Certifications (comma separated)"
                  value={Array.isArray(formData.certifications) ? formData.certifications.join(', ') : (formData.certifications || '')}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const arr = raw ? raw.split(',').map((c) => c.trim()).filter(Boolean) : [];
                    setFormData({ ...formData, certifications: arr });
                  }}
                />
              </Grid>
            </>
          ) : effectiveType.fields.map((field) => {
            // Special handling for assemblyType field in assembly-type-configurations
            if (field === 'assemblyType' && currentType.key === 'assembly-type-configurations') {
              return [
                // ATA Chapter dropdown - must be selected first
                <Grid item xs={12} key="ata-chapter">
                  <Autocomplete
                    options={ataChapters || []}
                    getOptionLabel={(option) => `${option.code} - ${option.name}`}
                    value={selectedAtaChapter}
                    onChange={handleAtaChapterChange}
                    disabled={!!selectedItem}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="ATA Chapter *"
                        required
                        sx={{
                          '& .MuiInputLabel-root': {
                            fontSize: '14px',
                            fontWeight: 600
                          },
                          '& .MuiOutlinedInput-root': {
                            fontSize: '14px'
                          }
                        }}
                        helperText={selectedItem ? 'ATA Chapter cannot be changed when editing' : 'Select an ATA Chapter first to filter Assembly Types'}
                      />
                    )}
                    sx={{
                      '& .MuiAutocomplete-inputRoot': {
                        padding: '14px 14px 14px 14px'
                      }
                    }}
                    ListboxProps={{
                      style: {
                        maxHeight: 400
                      }
                    }}
                  />
                </Grid>,
                // Assembly Type dropdown - filtered by ATA Chapter
                <Grid item xs={12} key="assembly-type">
                  <FormControl fullWidth required>
                    <InputLabel 
                      id="assembly-type-config-label"
                      sx={{ 
                        fontSize: '14px', 
                        fontWeight: 600,
                        color: 'text.primary',
                        backgroundColor: 'background.paper',
                        padding: '0 4px',
                        '&.MuiInputLabel-shrink': {
                          fontSize: '14px',
                          fontWeight: 600,
                          transform: 'translate(14px, -9px) scale(0.75)',
                          backgroundColor: 'background.paper',
                          padding: '0 4px'
                        },
                        '&.Mui-focused': {
                          color: 'primary.main',
                          fontWeight: 600
                        },
                        '&.MuiInputLabel-root': {
                          zIndex: 1
                        }
                      }}
                    >
                      Assembly Type *
                    </InputLabel>
                    <Select
                      labelId="assembly-type-config-label"
                      value={
                        typeof formData.assemblyType === 'object' && formData.assemblyType?._id
                          ? formData.assemblyType._id
                          : formData.assemblyType || ''
                      }
                      label="Assembly Type *"
                      onChange={(e) => setFormData({ ...formData, assemblyType: e.target.value })}
                      disabled={!!selectedItem || !selectedAtaChapterId}
                      sx={{ 
                        fontSize: '14px',
                        '& .MuiSelect-select': {
                          padding: '14px',
                          minHeight: '1.4375em'
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '14px',
                          fontWeight: 600
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23)'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.87)'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                          borderWidth: '2px'
                        }
                      }}
                      displayEmpty
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 400,
                          },
                        },
                      }}
                    >
                      <MenuItem value="" sx={{ fontSize: '14px', minHeight: '48px' }}>
                        <em>
                          {!selectedAtaChapterId 
                            ? 'Select an ATA Chapter first' 
                            : selectedItem 
                              ? 'Assembly Type (cannot be changed when editing)' 
                              : 'Select an Assembly Type'}
                        </em>
                      </MenuItem>
                      {(() => {
                        // Filter out assembly types that already have configurations (unless editing)
                        const existingConfigAssemblyTypeIds = new Set(
                          (assemblyTypeConfigurations || [])
                            .filter(config => {
                              // When editing, include the current item's assembly type
                              if (selectedItem && config._id === selectedItem._id) {
                                return false; // Don't filter out the current configuration's assembly type
                              }
                              return true;
                            })
                            .map(config => {
                              const assemblyTypeId = typeof config.assemblyType === 'object' 
                                ? config.assemblyType._id 
                                : config.assemblyType;
                              return String(assemblyTypeId);
                            })
                        );
                        
                        return filteredAssemblyTypes
                          .filter(at => {
                            // When editing, always show the current assembly type
                            if (selectedItem) {
                              const currentAssemblyTypeId = typeof formData.assemblyType === 'object' && formData.assemblyType?._id
                                ? String(formData.assemblyType._id)
                                : String(formData.assemblyType || '');
                              if (String(at._id) === currentAssemblyTypeId) {
                                return true;
                              }
                            }
                            // When adding, filter out assembly types with existing configurations
                            return !existingConfigAssemblyTypeIds.has(String(at._id));
                          })
                          .map((at) => (
                            <MenuItem key={at._id} value={at._id} sx={{ fontSize: '14px', minHeight: '48px', padding: '12px 16px' }}>
                              {at.name}
                            </MenuItem>
                          ));
                      })()}
                    </Select>
                  </FormControl>
                </Grid>
              ];
            }
            
            return (
              <Grid item xs={12} key={field}>
                {field === 'personToContact' && currentType.key === 'operators' ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Person to Contact
                  </Typography>
                  <Stack spacing={1.5}>
                    {(Array.isArray(formData.personToContact) ? formData.personToContact : []).map((person, index) => (
                      <Grid container spacing={1} key={`contact-${index}`}>
                        <Grid item xs={12} sm={5}>
                          <TextField
                            fullWidth
                            label="Person Name"
                            value={person.name || ''}
                            onChange={(e) => {
                              const next = [...(formData.personToContact || [])];
                              next[index] = { ...next[index], name: e.target.value };
                              setFormData({ ...formData, personToContact: next });
                            }}
                            required
                          />
                        </Grid>
                        <Grid item xs={12} sm={5}>
                          <TextField
                            fullWidth
                            label="Person Number"
                            value={person.number || ''}
                            onChange={(e) => {
                              const next = [...(formData.personToContact || [])];
                              next[index] = { ...next[index], number: e.target.value };
                              setFormData({ ...formData, personToContact: next });
                            }}
                            required
                          />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <Button
                            fullWidth
                            color="error"
                            variant="outlined"
                            onClick={() => {
                              const current = Array.isArray(formData.personToContact) ? [...formData.personToContact] : [];
                              if (current.length <= 1) return;
                              current.splice(index, 1);
                              setFormData({ ...formData, personToContact: current });
                            }}
                            disabled={!Array.isArray(formData.personToContact) || formData.personToContact.length <= 1}
                            sx={{ height: '56px' }}
                          >
                            Remove
                          </Button>
                        </Grid>
                      </Grid>
                    ))}
                  </Stack>
                  <Box sx={{ mt: 1 }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        const next = Array.isArray(formData.personToContact) ? [...formData.personToContact] : [];
                        next.push({ name: '', number: '' });
                        setFormData({ ...formData, personToContact: next });
                      }}
                    >
                      Add Person
                    </Button>
                  </Box>
                </Box>
              ) : field === 'template' && currentType.key === 'operators' ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Template Header (Letterpad)
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                    <Button
                      variant="outlined"
                      component="label"
                      disabled={letterpadUploading}
                    >
                      {letterpadUploading ? 'Uploading...' : 'Upload Letterpad'}
                      <input
                        hidden
                        type="file"
                        accept="image/*"
                        onChange={async (event) => {
                          const file = event.target.files?.[0];
                          if (!file) return;
                          try {
                            setLetterpadUploading(true);
                            const payload = new FormData();
                            payload.append('file', file);
                            payload.append('entityType', 'OPERATOR_TEMPLATE');
                            payload.append('kind', 'DOC');
                            const response = await axios.post('/api/media/upload', payload, {
                              headers: { 'Content-Type': 'multipart/form-data' }
                            });
                            setFormData({
                              ...formData,
                              template: response.data?.url || response.data?.fileUrl || '',
                              templateFileName: file.name
                            });
                          } catch (error) {
                            alert(error.response?.data?.message || 'Failed to upload letterpad. Please try again.');
                          } finally {
                            setLetterpadUploading(false);
                            event.target.value = '';
                          }
                        }}
                      />
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      {formData.templateFileName || (formData.template ? 'Letterpad uploaded' : 'No file selected')}
                    </Typography>
                  </Stack>
                </Box>
              ) : field === 'workOrderFormTemplate' && currentType.key === 'operators' ? (
                <FormControl fullWidth>
                  <InputLabel>WO Form Template</InputLabel>
                  <Select
                    value={formData.workOrderFormTemplate || 'CAME_FORM_04'}
                    label="WO Form Template"
                    onChange={(e) => setFormData({ ...formData, workOrderFormTemplate: e.target.value })}
                  >
                    <MenuItem value="CAME_FORM_04">CAME/FORM/04 (work package)</MenuItem>
                    <MenuItem value="GENERIC_PACKAGE">Generic work package</MenuItem>
                  </Select>
                </FormControl>
              ) : field === 'type' && currentType.key !== 'models' ? (
                <FormControl fullWidth required>
                  <InputLabel>Type *</InputLabel>
                  <Select
                    value={formData[field] || ''}
                    label="Type *"
                    onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                    required
                  >
                    <MenuItem value="">
                      <em>Select a type</em>
                    </MenuItem>
                    <MenuItem value="Fixed Wing">Fixed Wing</MenuItem>
                    <MenuItem value="Rotary Wing">Rotary Wing</MenuItem>
                  </Select>
                </FormControl>
              ) : field === 'description' || field === 'contactInfo' ? (
                <TextField
                  fullWidth
                  label={field.charAt(0).toUpperCase() + field.slice(1)}
                  value={formData[field] || ''}
                  onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                  multiline
                  rows={3}
                />
              ) : field === 'columns' && currentType.key === 'assembly-type-configurations' ? (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Column Definitions
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Add />}
                      onClick={handleAddColumn}
                    >
                      Add Column
                    </Button>
                  </Box>
                  
                  {Array.isArray(formData.columns) && formData.columns.length > 0 ? (
                    <Box sx={{ mb: 2 }}>
                      {formData.columns.map((column, index) => (
                        <Card key={index} sx={{ mb: 2, border: '1px solid #e0e0e0' }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="subtitle2" fontWeight="bold">
                                Column {index + 1}
                              </Typography>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveColumn(index)}
                                disabled={formData.columns.length === 1}
                              >
                                <Remove />
                              </IconButton>
                            </Box>
                            <Box sx={{ mb: 2 }}>
                              <FormControl fullWidth>
                                <InputLabel>Common Dataset</InputLabel>
                                <Select
                                  value={commonDatasetSelections[index] || ''}
                                  label="Common Dataset"
                                  onChange={(e) => {
                                    const template = e.target.value;
                                    setCommonDatasetSelections({ ...commonDatasetSelections, [index]: template });
                                    
                                    if (template) {
                                      const templates = {
                                        'landings': { name: 'landings', label: 'Number of Landings', type: 'number', unit: 'Nos' },
                                        'cycles': { name: 'cycles', label: 'Number of Cycles', type: 'number', unit: 'Cls' },
                                        'hours': { name: 'hours', label: 'Hours', type: 'number', unit: 'Hrs' },
                                        'years': { name: 'years', label: 'Number of Years', type: 'number', unit: 'Yrs' },
                                        'days': { name: 'days', label: 'Number of Days', type: 'number', unit: 'Days' },
                                        'months': { name: 'months', label: 'Number of Months', type: 'number', unit: 'Months' }
                                      };
                                      const selectedTemplate = templates[template];
                                      if (selectedTemplate) {
                                        // Update all fields at once to avoid state update issues
                                        const currentColumns = Array.isArray(formData.columns) ? [...formData.columns] : [];
                                        const updatedColumn = {
                                          ...currentColumns[index],
                                          name: selectedTemplate.name,
                                          label: selectedTemplate.label,
                                          type: selectedTemplate.type,
                                          unit: selectedTemplate.unit
                                        };
                                        currentColumns[index] = updatedColumn;
                                        setFormData({ ...formData, columns: currentColumns });
                                      }
                                      // Reset dropdown selection after a short delay
                                      setTimeout(() => {
                                        setCommonDatasetSelections(prev => ({ ...prev, [index]: '' }));
                                      }, 100);
                                    }
                                  }}
                                  displayEmpty
                                >
                                  <MenuItem value="">
                                    <em>Select a template to auto-fill</em>
                                  </MenuItem>
                                  <MenuItem value="landings">Landings</MenuItem>
                                  <MenuItem value="cycles">Cycles</MenuItem>
                                  <MenuItem value="hours">Hours</MenuItem>
                                  <MenuItem value="years">Years</MenuItem>
                                  <MenuItem value="days">Days</MenuItem>
                                  <MenuItem value="months">Months</MenuItem>
                                </Select>
                              </FormControl>
                            </Box>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  fullWidth
                                  label="Name *"
                                  value={column.name || ''}
                                  onChange={(e) => handleColumnChange(index, 'name', e.target.value)}
                                  placeholder="e.g., hours"
                                  required
                                  helperText="Field name (used in code)"
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  fullWidth
                                  label="Label *"
                                  value={column.label || ''}
                                  onChange={(e) => handleColumnChange(index, 'label', e.target.value)}
                                  placeholder="e.g., Hours"
                                  required
                                  helperText="Display label"
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required>
                                  <InputLabel>Type *</InputLabel>
                                  <Select
                                    value={column.type || 'text'}
                                    label="Type *"
                                    onChange={(e) => handleColumnChange(index, 'type', e.target.value)}
                                  >
                                    <MenuItem value="text">Text</MenuItem>
                                    <MenuItem value="number">Number</MenuItem>
                                    <MenuItem value="date">Date</MenuItem>
                                    <MenuItem value="time">Time</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  fullWidth
                                  label="Unit"
                                  value={column.unit || ''}
                                  onChange={(e) => handleColumnChange(index, 'unit', e.target.value)}
                                  placeholder="e.g., hrs, HH:MM"
                                  helperText="Optional unit of measurement"
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  fullWidth
                                  type="number"
                                  label="Order *"
                                  value={column.order || index + 1}
                                  onChange={(e) => handleColumnChange(index, 'order', parseInt(e.target.value) || index + 1)}
                                  required
                                  helperText="Display order"
                                  inputProps={{ min: 1 }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={column.required || false}
                                      onChange={(e) => handleColumnChange(index, 'required', e.target.checked)}
                                    />
                                  }
                                  label="Required Field"
                                />
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                      No columns defined. Click "Add Column" to get started.
                    </Typography>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      JSON Preview
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={6}
                      value={
                        Array.isArray(formData.columns)
                          ? JSON.stringify(formData.columns, null, 2)
                          : '[]'
                      }
                      InputProps={{
                        readOnly: true,
                        sx: { fontFamily: 'monospace', fontSize: '0.875rem' }
                      }}
                      helperText="This is a preview of the JSON that will be saved. It updates automatically as you edit columns above."
                    />
                  </Box>
                </Box>
              ) : field === 'assemblyType' && currentType.key === 'assembly-type-configurations' ? (
                // This case is handled above in the special handling section
                null
              ) : field === 'manufacturer' && currentType.key === 'manufacturers' && manufacturerSubTab === 3 ? (
                <FormControl fullWidth required>
                  <InputLabel 
                    id="model-variant-manufacturer-label" 
                    sx={{ 
                      fontSize: '14px', 
                      fontWeight: 600,
                      color: 'text.primary',
                      backgroundColor: 'background.paper',
                      padding: '0 4px',
                      '&.MuiInputLabel-shrink': {
                        fontSize: '14px',
                        fontWeight: 600,
                        transform: 'translate(14px, -9px) scale(0.75)',
                        backgroundColor: 'background.paper',
                        padding: '0 4px'
                      },
                      '&.Mui-focused': {
                        color: 'primary.main',
                        fontWeight: 600
                      },
                      '&.MuiInputLabel-root': {
                        zIndex: 1
                      }
                    }}
                  >
                    Manufacturer *
                  </InputLabel>
                  <Select
                    labelId="model-variant-manufacturer-label"
                    value={
                      typeof formData.manufacturer === 'object' && formData.manufacturer?._id
                        ? formData.manufacturer._id
                        : formData.manufacturer || ''
                    }
                    label="Manufacturer *"
                    onChange={(e) => {
                      // Clear primaryModel when manufacturer changes
                      setFormData({ ...formData, manufacturer: e.target.value, primaryModel: '' });
                    }}
                    required
                    sx={{ 
                      fontSize: '14px',
                      '& .MuiSelect-select': {
                        padding: '14px',
                        minHeight: '1.4375em'
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '14px',
                        fontWeight: 600
                      }
                    }}
                    displayEmpty
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 400,
                        },
                      },
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: '14px', minHeight: '48px' }}>
                      <em>Select a manufacturer</em>
                    </MenuItem>
                    {manufacturerNames.map((mfr) => (
                      <MenuItem key={mfr._id} value={mfr._id} sx={{ fontSize: '14px', minHeight: '48px', padding: '12px 16px' }}>
                        {mfr.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : field === 'primaryModel' && currentType.key === 'manufacturers' && manufacturerSubTab === 3 ? (
                <FormControl fullWidth required>
                  <InputLabel 
                    id="model-variant-primary-model-label" 
                    sx={{ 
                      fontSize: '14px', 
                      fontWeight: 600,
                      color: 'text.primary',
                      backgroundColor: 'background.paper',
                      padding: '0 4px',
                      '&.MuiInputLabel-shrink': {
                        fontSize: '14px',
                        fontWeight: 600,
                        transform: 'translate(14px, -9px) scale(0.75)',
                        backgroundColor: 'background.paper',
                        padding: '0 4px'
                      },
                      '&.Mui-focused': {
                        color: 'primary.main',
                        fontWeight: 600
                      },
                      '&.MuiInputLabel-root': {
                        zIndex: 1
                      }
                    }}
                  >
                    Primary Model *
                  </InputLabel>
                  <Select
                    labelId="model-variant-primary-model-label"
                    value={
                      typeof formData.primaryModel === 'object' && formData.primaryModel?._id
                        ? formData.primaryModel._id
                        : formData.primaryModel || ''
                    }
                    label="Primary Model *"
                    onChange={(e) => setFormData({ ...formData, primaryModel: e.target.value })}
                    required
                    disabled={!formData.manufacturer}
                    sx={{ 
                      fontSize: '14px',
                      '& .MuiSelect-select': {
                        padding: '14px',
                        minHeight: '1.4375em'
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '14px',
                        fontWeight: 600
                      }
                    }}
                    displayEmpty
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 400,
                        },
                      },
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: '14px', minHeight: '48px' }}>
                      <em>{formData.manufacturer ? 'Select a primary model' : 'Select manufacturer first'}</em>
                    </MenuItem>
                    {models
                      .filter(m => {
                        // Filter to show only Primary Models (have type and no forAssembly)
                        const isPrimaryModel = m.type && (!m.forAssembly || m.forAssembly === '');
                        // Filter by selected manufacturer
                        const matchesManufacturer = formData.manufacturer 
                          ? (typeof m.manufacturer === 'object' ? m.manufacturer._id : m.manufacturer) === formData.manufacturer
                          : true;
                        return isPrimaryModel && matchesManufacturer && m._id !== selectedItem?._id;
                      })
                      .map((model) => (
                        <MenuItem key={model._id} value={model._id} sx={{ fontSize: '14px', minHeight: '48px', padding: '12px 16px' }}>
                          {model.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              ) : field === 'ataChapterId' && currentType.key === 'assembly-types' ? (
                <Autocomplete
                  options={ataChapters || []}
                  getOptionLabel={(option) => `${option.code} - ${option.name}`}
                  value={
                    formData.ataChapterId
                      ? (typeof formData.ataChapterId === 'object' && formData.ataChapterId?._id
                          ? ataChapters.find(ch => String(ch._id) === String(formData.ataChapterId._id)) || null
                          : ataChapters.find(ch => String(ch._id) === String(formData.ataChapterId)) || null)
                      : null
                  }
                  onChange={(event, newValue) => {
                    setFormData({ 
                      ...formData, 
                      ataChapterId: newValue ? newValue._id : undefined 
                    });
                  }}
                  disabled={formData.name?.toLowerCase() === 'airframe'}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`ATA Chapter ${formData.name?.toLowerCase() !== 'airframe' ? '*' : ''}`}
                      required={formData.name?.toLowerCase() !== 'airframe'}
                      sx={{
                        '& .MuiInputLabel-root': {
                          fontSize: '14px',
                          fontWeight: 600
                        },
                        '& .MuiOutlinedInput-root': {
                          fontSize: '14px'
                        }
                      }}
                      helperText={formData.name?.toLowerCase() === 'airframe' ? 'Not required for Airframe' : 'Type to search for an ATA Chapter'}
                    />
                  )}
                  sx={{
                    '& .MuiAutocomplete-inputRoot': {
                      padding: '14px 14px 14px 14px'
                    }
                  }}
                  ListboxProps={{
                    style: {
                      maxHeight: 400
                    }
                  }}
                />
              ) : field === 'manufacturer' && currentType.key === 'manufacturers' && manufacturerSubTab === 2 ? (
                <FormControl fullWidth required>
                  <InputLabel 
                    id="manufacturer-name-label" 
                    sx={{ 
                      fontSize: '14px', 
                      fontWeight: 600,
                      color: 'text.primary',
                      backgroundColor: 'background.paper',
                      padding: '0 4px',
                      '&.MuiInputLabel-shrink': {
                        fontSize: '14px',
                        fontWeight: 600,
                        transform: 'translate(14px, -9px) scale(0.75)',
                        backgroundColor: 'background.paper',
                        padding: '0 4px'
                      },
                      '&.Mui-focused': {
                        color: 'primary.main',
                        fontWeight: 600
                      },
                      '&.MuiInputLabel-root': {
                        zIndex: 1
                      }
                    }}
                  >
                    Manufacturer Name *
                  </InputLabel>
                  <Select
                    labelId="manufacturer-name-label"
                    value={
                      typeof formData.manufacturer === 'object' && formData.manufacturer?._id
                        ? formData.manufacturer._id
                        : formData.manufacturer || ''
                    }
                    label="Manufacturer Name *"
                    onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                    required
                    sx={{ 
                      fontSize: '14px',
                      '& .MuiSelect-select': {
                        padding: '14px',
                        minHeight: '1.4375em'
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '14px',
                        fontWeight: 600
                      }
                    }}
                    displayEmpty
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 400,
                        },
                      },
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: '14px', minHeight: '48px' }}>
                      <em>Select a manufacturer name</em>
                    </MenuItem>
                    {manufacturerNames.map((name) => (
                      <MenuItem key={name._id} value={name._id} sx={{ fontSize: '14px', minHeight: '48px', padding: '12px 16px' }}>
                        {name.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : field === 'category' && currentType.key === 'manufacturers' && manufacturerSubTab === 2 ? (
                <FormControl fullWidth required>
                  <InputLabel 
                    id="aircraft-category-label" 
                    sx={{ 
                      fontSize: '14px', 
                      fontWeight: 600,
                      color: 'text.primary',
                      backgroundColor: 'background.paper',
                      padding: '0 4px',
                      '&.MuiInputLabel-shrink': {
                        fontSize: '14px',
                        fontWeight: 600,
                        transform: 'translate(14px, -9px) scale(0.75)',
                        backgroundColor: 'background.paper',
                        padding: '0 4px'
                      },
                      '&.Mui-focused': {
                        color: 'primary.main',
                        fontWeight: 600
                      },
                      '&.MuiInputLabel-root': {
                        zIndex: 1
                      }
                    }}
                  >
                    Aircraft Category *
                  </InputLabel>
                  <Select
                    labelId="aircraft-category-label"
                    value={
                      typeof formData.category === 'object' && formData.category?._id
                        ? formData.category._id
                        : formData.category || ''
                    }
                    label="Aircraft Category *"
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                    sx={{ 
                      fontSize: '14px',
                      '& .MuiSelect-select': {
                        padding: '14px',
                        minHeight: '1.4375em'
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '14px',
                        fontWeight: 600
                      }
                    }}
                    displayEmpty
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 400,
                        },
                      },
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: '14px', minHeight: '48px' }}>
                      <em>Select an aircraft category</em>
                    </MenuItem>
                    {manufacturerCategories.map((cat) => (
                      <MenuItem key={cat._id} value={cat._id} sx={{ fontSize: '14px', minHeight: '48px', padding: '12px 16px' }}>
                        {cat.category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : field === 'type' && currentType.key === 'manufacturers' && manufacturerSubTab === 2 ? (
                <FormControl fullWidth required>
                  <InputLabel 
                    id="aircraft-type-label" 
                    sx={{ 
                      fontSize: '14px', 
                      fontWeight: 600,
                      color: 'text.primary',
                      backgroundColor: 'background.paper',
                      padding: '0 4px',
                      '&.MuiInputLabel-shrink': {
                        fontSize: '14px',
                        fontWeight: 600,
                        transform: 'translate(14px, -9px) scale(0.75)',
                        backgroundColor: 'background.paper',
                        padding: '0 4px'
                      },
                      '&.Mui-focused': {
                        color: 'primary.main',
                        fontWeight: 600
                      },
                      '&.MuiInputLabel-root': {
                        zIndex: 1
                      }
                    }}
                  >
                    Aircraft Type *
                  </InputLabel>
                  <Select
                    labelId="aircraft-type-label"
                    value={formData[field] || ''}
                    label="Aircraft Type *"
                    onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                    required
                    sx={{ 
                      fontSize: '14px',
                      '& .MuiSelect-select': {
                        padding: '14px',
                        minHeight: '1.4375em'
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '14px',
                        fontWeight: 600
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.23)'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.87)'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                        borderWidth: '2px'
                      }
                    }}
                    displayEmpty
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 400,
                        },
                      },
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: '14px', minHeight: '48px' }}>
                      <em>Select an aircraft type</em>
                    </MenuItem>
                    <MenuItem value="Fixed Wing" sx={{ fontSize: '14px', minHeight: '48px', padding: '12px 16px' }}>Fixed Wing</MenuItem>
                    <MenuItem value="Rotary Wing" sx={{ fontSize: '14px', minHeight: '48px', padding: '12px 16px' }}>Rotary Wing</MenuItem>
                  </Select>
                </FormControl>
              ) : field === 'primaryCategory' && currentType.key === 'part-categories' ? (
                <FormControl fullWidth>
                  <InputLabel 
                    id="primary-category-label"
                    sx={{ 
                      fontSize: '14px', 
                      fontWeight: 600,
                      color: 'text.primary',
                      backgroundColor: 'background.paper',
                      padding: '0 4px',
                      '&.MuiInputLabel-shrink': {
                        fontSize: '14px',
                        fontWeight: 600,
                        transform: 'translate(14px, -9px) scale(0.75)',
                        backgroundColor: 'background.paper',
                        padding: '0 4px'
                      },
                      '&.Mui-focused': {
                        color: 'primary.main',
                        fontWeight: 600
                      },
                      '&.MuiInputLabel-root': {
                        zIndex: 1
                      }
                    }}
                  >
                    Primary Category
                  </InputLabel>
                  <Select
                    labelId="primary-category-label"
                    value={
                      typeof formData.primaryCategory === 'object' && formData.primaryCategory?._id
                        ? formData.primaryCategory._id
                        : formData.primaryCategory || ''
                    }
                    label="Primary Category"
                    onChange={(e) => setFormData({ ...formData, primaryCategory: e.target.value || undefined })}
                    sx={{ 
                      fontSize: '14px',
                      '& .MuiSelect-select': {
                        padding: '14px',
                        minHeight: '1.4375em'
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '14px',
                        fontWeight: 600
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.23)'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.87)'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                        borderWidth: '2px'
                      }
                    }}
                    displayEmpty
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 400,
                        },
                      },
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: '14px', minHeight: '48px' }}>
                      <em>None (Top-level category)</em>
                    </MenuItem>
                    {partCategories
                      .filter(cat => {
                        // Don't show the current item being edited as an option for its own primary category
                        if (selectedItem && String(cat._id) === String(selectedItem._id)) return false;
                        // Only show top-level categories (categories without a primaryCategory)
                        // primaryCategory can be null, undefined, or an empty string
                        const hasPrimaryCategory = cat.primaryCategory && 
                          (typeof cat.primaryCategory === 'object' ? cat.primaryCategory._id : cat.primaryCategory);
                        return !hasPrimaryCategory;
                      })
                      .map((category) => (
                        <MenuItem key={category._id} value={category._id} sx={{ fontSize: '14px', minHeight: '48px', padding: '12px 16px' }}>
                          {category.name} {category.glCode ? `(${category.glCode})` : ''}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  fullWidth
                  label={
                    currentType.key === 'operators' && field === 'gmail'
                      ? 'Gmail'
                      : currentType.key === 'operators' && field === 'number'
                        ? 'Number'
                        : field.charAt(0).toUpperCase() + field.slice(1)
                  }
                  value={
                    typeof formData[field] === 'object' && formData[field] !== null
                      ? formData[field]._id || formData[field].name || formData[field].category || formData[field].code || ''
                      : formData[field] || ''
                  }
                  onChange={(e) => {
                    const newValue = e.target.value;
                    const updatedFormData = { ...formData, [field]: newValue };
                    
                    if (currentType.key === 'assembly-types' && field === 'name' && newValue.trim().toLowerCase() === 'airframe') {
                      updatedFormData.ataChapterId = undefined;
                    }
                    
                    setFormData(updatedFormData);
                  }}
                  required={
                    ['category', 'name', 'licenseNumber', 'employeeName'].includes(field) || 
                    (currentType.key === 'manufacturers' && manufacturerSubTab === 1 && field === 'category') ||
                    (currentType.key === 'manufacturers' && manufacturerSubTab === 0 && field === 'name') ||
                    (currentType.key === 'manufacturers' && manufacturerSubTab === 2 && (field === 'name' || field === 'manufacturer' || field === 'category' || field === 'type')) ||
                    (currentType.key === 'operators' && ['name', 'code', 'address', 'gmail', 'number', 'personToContact', 'template'].includes(field))
                  }
                />
              )}
              </Grid>
            );
          })}
          {/* Show readonly Aircraft Category and Aircraft Type for Model Variants when Primary Model is selected */}
          {currentType.key === 'manufacturers' && manufacturerSubTab === 3 && formData.primaryModel && (() => {
            const selectedPrimaryModel = models.find(m => {
              const modelId = typeof m._id === 'string' ? m._id : String(m._id);
              const primaryModelId = typeof formData.primaryModel === 'string' 
                ? formData.primaryModel 
                : String(formData.primaryModel);
              return modelId === primaryModelId;
            });
            const categoryName = selectedPrimaryModel?.category 
              ? (typeof selectedPrimaryModel.category === 'object' 
                  ? selectedPrimaryModel.category.category 
                  : selectedPrimaryModel.category)
              : '-';
            const typeName = selectedPrimaryModel?.type || '-';
            
            return (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Aircraft Category"
                    value={categoryName}
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        backgroundColor: 'action.disabledBackground',
                        cursor: 'not-allowed'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Aircraft Type"
                    value={typeName}
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        backgroundColor: 'action.disabledBackground',
                        cursor: 'not-allowed'
                      }
                    }}
                  />
                </Grid>
              </>
            );
          })()}
        </Grid>
        {currentType.key === 'part-categories' && (
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontStyle: 'italic' }}>
              GL Code: General Ledger code for accounting integration and financial reporting (e.g., GL-5100)
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onSubmit} variant="contained">
          {selectedItem ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MasterDataFormDialog;
